import {
  CheckCircleIcon,
  KeyIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RecorderStatusCell } from '@/sections/asset/components/tabs/credentials/RecorderProgress';
import type { RecorderJob } from '@/sections/asset/components/tabs/credentials/RecorderProgress/useRecorderJobs';
import { Credential } from '@/types';

function getCredentialTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    basic: 'Automated',
    recording: 'Recording',
    bearer_token: 'Bearer Token',
    api_key: 'API Key',
    static_token: 'Static Headers',
    recipe: 'Login Recipe',
    complex_login: 'Complex Login',
    'web-auth': 'Web Auth',
  };
  return labels[type] ?? type;
}

interface CredentialListModalProps {
  open: boolean;
  onClose: () => void;
  credentials: Credential[];
  onAddClick: () => void;
  onDelete: (credential: Credential) => void;
  canAddCredentials?: boolean;
  addDisabledTooltip?: string;
  /** The credentialId of the currently-default credential */
  defaultCredentialId?: string;
  /** Callback to set a credential as default */
  onSetDefault?: (credential: Credential) => void;
  /** Latest recorder-agent run per credentialId, for the Status cell */
  recorderJobs?: Map<string, RecorderJob>;
  /** Re-open the recorder progress modal for a credential's run */
  onOpenRecorder?: (conversationId: string, label: string) => void;
}

export function CredentialListModal({
  open,
  onClose,
  credentials,
  onAddClick,
  onDelete,
  canAddCredentials = true,
  addDisabledTooltip,
  defaultCredentialId,
  onSetDefault,
  recorderJobs,
  onOpenRecorder,
}: CredentialListModalProps) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  return (
    <Modal
      title="Manage Credentials"
      open={open}
      onClose={onClose}
      size="xl"
      footer={{
        text: 'Add Credential',
        onClick: onAddClick,
        disabled: !canAddCredentials,
        disabledTooltip: !canAddCredentials ? addDisabledTooltip : undefined,
        'data-testid': 'credential-list-add-button',
      }}
    >
      <div className="flex flex-col" data-testid="credential-list-modal">
        {credentials.length === 0 && (
          <div className="text-chariot-text-secondary flex flex-col items-center justify-center gap-2 p-8 text-center">
            <KeyIcon className="size-8 opacity-40" />
            <span className="text-sm">No credentials configured</span>
            <span className="text-xs">
              Add a credential to enable authenticated scanning
            </span>
          </div>
        )}
        {credentials.map(credential => (
          <div
            key={credential.credentialId}
            className="border-chariot-stroke flex items-center justify-between gap-4 border-b px-1 py-3 last:border-b-0"
            data-testid={`credential-list-row-${credential.credentialId}`}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {getCredentialTypeLabel(credential.type)}
                </Badge>
                <span className="text-chariot-text-primary truncate text-sm font-medium">
                  {credential.name}
                </span>
                {credential.credentialId === defaultCredentialId && (
                  <span
                    className="bg-chariot-brand-primary/10 text-chariot-brand-primary inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                    data-testid={`credential-default-badge-${credential.credentialId}`}
                  >
                    <CheckCircleIcon className="size-3" />
                    Default
                  </span>
                )}
              </div>
              {credential.history?.[0]?.by && (
                <span className="text-chariot-text-secondary text-xs">
                  Added by {credential.history[0].by}
                  {credential.created && (
                    <>
                      {' '}
                      ·{' '}
                      {new Date(credential.created).toLocaleDateString(
                        'en-US',
                        { month: 'short', day: 'numeric', year: 'numeric' }
                      )}
                    </>
                  )}
                </span>
              )}
            </div>
            {onOpenRecorder && (
              <RecorderStatusCell
                recorder={recorderJobs?.get(credential.credentialId)}
                onOpen={onOpenRecorder}
              />
            )}
            {confirmingId === credential.credentialId ? (
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-secondary text-xs">
                  Delete &quot;{credential.name}&quot;?
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    onDelete(credential);
                    setConfirmingId(null);
                  }}
                  data-testid={`credential-confirm-delete-${credential.credentialId}`}
                >
                  Confirm
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingId(null)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                {onSetDefault &&
                  credential.credentialId !== defaultCredentialId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSetDefault(credential)}
                      data-testid={`credential-set-default-${credential.credentialId}`}
                    >
                      <StarIcon className="size-4" />
                      Set as Default
                    </Button>
                  )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingId(credential.credentialId)}
                  data-testid={`credential-delete-${credential.credentialId}`}
                >
                  <TrashIcon className="size-4" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
